import React, { useEffect } from "react"
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Stack,
  Paper,
  Icon,
  Chip,
  Divider,
  Link as MuiLink,
} from "@mui/material"
import PlayCircleIcon from "@mui/icons-material/PlayCircle"
import CelebrationIcon from "@mui/icons-material/Celebration"
import ShareIcon from "@mui/icons-material/Share"
import MovieIcon from "@mui/icons-material/Movie"
import GroupsIcon from "@mui/icons-material/Groups"
import VisibilityIcon from "@mui/icons-material/Visibility"
import EventIcon from "@mui/icons-material/Event"
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import StarIcon from "@mui/icons-material/Star"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import InventoryIcon from "@mui/icons-material/Inventory"
import ChildFriendlyIcon from "@mui/icons-material/ChildFriendly"

const colors = {
  primary: "#705d00",
  primaryContainer: "#ffd700",
  onPrimary: "#ffffff",
  secondary: "#006a65",
  secondaryContainer: "#76f3ea",
  onSecondary: "#ffffff",
  background: "#f8f9fa",
  surface: "#f8f9fa",
  surfaceContainerLow: "#f3f4f5",
  surfaceContainerLowest: "#ffffff",
  onSurfaceVariant: "#4d4732",
  onBackground: "#191c1d",
}

const fonts = {
  display: '"Bebas Neue", sans-serif',
  body: '"Plus Jakarta Sans", sans-serif',
  label: '"Space Grotesk", sans-serif',
}

const confettiBg =
  "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffd700' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"

const glassCard = {
  backdropFilter: "blur(12px)",
  background: "rgba(255,255,255,0.8)",
  border: "1px solid rgba(255,255,255,1)",
}

const glossyButton = {
  position: "relative",
  overflow: "hidden",
  background: "linear-gradient(135deg, #ffd700 0%, #e9c400 100%)",
  borderTop: "2px solid rgba(255,255,255,0.5)",
  color: colors.onPrimary,
  fontFamily: fonts.label,
  fontWeight: 700,
  letterSpacing: "0.05em",
  boxShadow: "0 15px 40px rgba(255,215,0,0.3)",
  "&:hover": {
    transform: "scale(1.05)",
    background: "linear-gradient(135deg, #ffd700 0%, #e9c400 100%)",
  },
  "&:active": { transform: "scale(0.95)" },
  transition: "all 0.3s",
  "&::after": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background:
      "linear-gradient(45deg, transparent 45%, rgba(255,255,255,0.3) 50%, transparent 55%)",
    transform: "rotate(-45deg)",
    pointerEvents: "none",
  },
}

const CastCard = ({ img, label, name, role, labelColor = colors.primary, onLabelColor = colors.onPrimary, labelRotate = 3, labelSide = "right", size = "lg" }) => {
  const isLg = size === "lg"
  const w = isLg ? 256 : 192
  const h = isLg ? 320 : 240
  return (
    <Stack alignItems="center">
      <Box sx={{ position: "relative", "&:hover .ring": { bgcolor: "rgba(255,215,0,0.4)" }, "&:hover img": { filter: "grayscale(0)", transform: "scale(1.05)" } }}>
        <Box
          className="ring"
          sx={{
            position: "absolute",
            inset: -16,
            borderRadius: "50%",
            bgcolor: "rgba(255,215,0,0.2)",
            filter: "blur(32px)",
            transition: "background-color .3s",
          }}
        />
        <Box
          component="img"
          src={img}
          alt={name}
          sx={{
            position: "relative",
            width: w,
            height: h,
            objectFit: "cover",
            borderRadius: 3,
            border: "4px solid #fff",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            filter: "grayscale(1)",
            transition: "all .5s",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -16,
            [labelSide]: -16,
            bgcolor: labelColor,
            color: onLabelColor,
            px: 3,
            py: 0.75,
            borderRadius: 1.5,
            fontFamily: fonts.label,
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.05em",
            transform: `rotate(${labelRotate}deg)`,
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
          }}
        >
          {label}
        </Box>
      </Box>
      <Typography sx={{ mt: 6, fontFamily: fonts.display, fontSize: isLg ? 32 : 28, color: colors.primary, lineHeight: 1.1 }}>{name}</Typography>
      <Typography sx={{ fontFamily: fonts.body, fontStyle: "italic", color: colors.onSurfaceVariant, mt: 0.5 }}>{role}</Typography>
    </Stack>
  )
}

const CountdownCard = ({ value, label, withStar }) => (
  <Paper
    elevation={0}
    sx={{
      ...glassCard,
      p: 5,
      borderRadius: 4,
      boxShadow: "0 20px 50px rgba(0,0,0,0.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {withStar && (
      <StarIcon sx={{ position: "absolute", top: 8, right: 8, color: colors.primary, opacity: 0.2, fontSize: 36 }} />
    )}
    <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 64, md: 96 }, color: colors.primary, lineHeight: 1, mb: 1, letterSpacing: "0.05em" }}>
      {value}
    </Typography>
    <Typography sx={{ fontFamily: fonts.label, fontSize: 14, fontWeight: 700, color: colors.onSurfaceVariant, letterSpacing: "0.2em" }}>{label}</Typography>
  </Paper>
)

const BigReveal = () => {
  useEffect(() => {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href =
      "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Plus+Jakarta+Sans:wght@400;500;700&family=Space+Grotesk:wght@700&display=swap"
    document.head.appendChild(link)
    return () => {
      document.head.removeChild(link)
    }
  }, [])

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.background,
        color: colors.onBackground,
        fontFamily: fonts.body,
        backgroundImage: confettiBg,
        "@keyframes shimmer": { to: { backgroundPosition: "200% center" } },
      }}
    >
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "rgba(248,249,250,0.8)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 10px 30px rgba(255,215,0,0.15)",
          color: colors.primary,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 8 }, py: 1 }}>
          <Typography sx={{ fontFamily: fonts.display, fontSize: 32, letterSpacing: "0.15em", color: colors.primary }}>
            THE BIG REVEAL
          </Typography>
          <Stack direction="row" spacing={4} sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
            {[
              { label: "Premiere", href: "#premiere", active: true },
              { label: "The Cast", href: "#cast" },
              { label: "Teaser", href: "#teaser" },
              { label: "Package Tracker", href: "#tracker" },
            ].map((item) => (
              <MuiLink
                key={item.label}
                href={item.href}
                underline="none"
                sx={{
                  fontFamily: fonts.label,
                  fontSize: 14,
                  fontWeight: 700,
                  color: item.active ? colors.primary : colors.onSurfaceVariant,
                  borderBottom: item.active ? `2px solid ${colors.primary}` : "none",
                  pb: 0.5,
                  "&:hover": { transform: "scale(1.05)" },
                  transition: "transform .3s",
                }}
              >
                {item.label}
              </MuiLink>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ color: colors.primary }}>
            <CelebrationIcon sx={{ cursor: "pointer", "&:hover": { transform: "scale(1.1)" }, transition: "transform .3s" }} />
            <ShareIcon sx={{ cursor: "pointer", "&:hover": { transform: "scale(1.1)" }, transition: "transform .3s" }} />
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="main">
        {/* Hero */}
        <Box
          component="section"
          id="premiere"
          sx={{
            scrollMarginTop: 80,
            position: "relative",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: 12,
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <Box
              component="img"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuApz74PsbT1BjvB8q56sF132e6NwNWH8NFEgaI7XFSvjJhJFq-7S_sUWajbMbvEPPLB0rptbxASBk4olzTmcEE_rw5480nzfCb-8u2FF9arQmJtVaUpsNAKxXisVk_KsAoWkXb4CA5GWaKSDYMp9qtkLrUQJoEcgFgDL7lcne29XSz5hg5ltlrdog8ibC-liQe7Tg2fviS5sH-AjOrBDgCIyzHgL2t7HQgonBnZxyYZmc7CkrKeSMqBkSTKt-8lyo3lAI051fBQEkCp"
              alt="Premiere stage"
              sx={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.3, filter: "grayscale(0.5)" }}
            />
            <Box sx={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, transparent, ${colors.background}, ${colors.background})` }} />
          </Box>
          <Container sx={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: "lg !important" }}>
            <Chip
              label="BLOCKBUSTER EVENT"
              sx={{
                mb: 3,
                bgcolor: "rgba(118,243,234,0.3)",
                color: colors.secondary,
                border: "1px solid rgba(0,106,101,0.2)",
                fontFamily: fonts.label,
                fontWeight: 700,
                letterSpacing: "0.05em",
                transform: "rotate(-2deg)",
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontFamily: fonts.display,
                fontSize: { xs: 40, md: 96 },
                lineHeight: 1,
                mb: 2,
                letterSpacing: "0.05em",
                background: "linear-gradient(90deg, #705d00 0%, #ffd700 50%, #705d00 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "shimmer 3s linear infinite",
                filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.1))",
              }}
            >
              THE PREMIERE OF THE CENTURY: COMING SOON.
            </Typography>
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontSize: 32,
                color: colors.onSurfaceVariant,
                letterSpacing: "0.1em",
                maxWidth: 640,
                mx: "auto",
                mb: 6,
              }}
            >
              Prepare for the ultimate world-shaking event.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} justifyContent="center" alignItems="center">
              <Button
                component="a"
                href="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ...glossyButton, px: 5, py: 2.5, borderRadius: 3, fontSize: 14 }}
                endIcon={<PlayCircleIcon />}
              >
                WATCH TRAILER
              </Button>
              <Button
                sx={{
                  ...glassCard,
                  px: 5,
                  py: 2.5,
                  borderRadius: 3,
                  color: colors.primary,
                  fontFamily: fonts.label,
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: "0.05em",
                  border: `1px solid rgba(112,93,0,0.2)`,
                  "&:hover": { transform: "scale(1.05)", background: "rgba(255,255,255,0.9)" },
                  "&:active": { transform: "scale(0.95)" },
                  transition: "all .3s",
                }}
              >
                GET THE FULL STORY
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Countdown */}
        <Box component="section" sx={{ py: 12, px: { xs: 2, md: 8 }, bgcolor: colors.surfaceContainerLow }}>
          <Container sx={{ textAlign: "center", maxWidth: "xl !important" }}>
            <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 40, md: 64 }, color: colors.primary, mb: 8, letterSpacing: "0.02em" }}>
              GLOBAL RELEASE COUNTDOWN
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}><CountdownCard value="238" label="DAYS" withStar /></Grid>
              <Grid item xs={6} md={3}><CountdownCard value="14" label="HOURS" /></Grid>
              <Grid item xs={6} md={3}><CountdownCard value="52" label="MINUTES" /></Grid>
              <Grid item xs={6} md={3}><CountdownCard value="09" label="SECONDS" /></Grid>
            </Grid>
          </Container>
        </Box>

        {/* The Cast */}
        <Box component="section" id="cast" sx={{ scrollMarginTop: 80, py: 12, px: { xs: 2, md: 8 } }}>
          <Container sx={{ maxWidth: "xl !important" }}>
            <Stack alignItems="center" sx={{ mb: 8 }}>
              <TheaterComedyIcon sx={{ color: colors.primary, fontSize: 48, mb: 2 }} />
              <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 40, md: 64 }, color: colors.primary, letterSpacing: "0.02em" }}>
                STARRING...
              </Typography>
            </Stack>
            <Grid container spacing={6} justifyContent="center" sx={{ maxWidth: 900, mx: "auto" }}>
              <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "center" }}>
                <CastCard
                  img="/images/matthew-reveal.png"
                  label="THE DAD"
                  name='MATTHEW "THE NATURAL"'
                  role="As the Expert Diaper Technician"
                  labelSide="right"
                  labelRotate={3}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "center" }}>
                <CastCard
                  img="/images/sadie-reveal.png"
                  label="THE MOM"
                  name='SADIE "THE VISIONARY"'
                  role="As the Master Producer of Life"
                  labelSide="left"
                  labelRotate={-3}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 10 }}>
              <Typography sx={{ fontFamily: fonts.display, fontSize: 32, color: "rgba(112,93,0,0.6)", textAlign: "center", mb: 6, letterSpacing: "0.2em" }}>
                FEATURING THE SUPPORTING CAST
              </Typography>
              <Grid container spacing={6} justifyContent="center" sx={{ maxWidth: 720, mx: "auto" }}>
                <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "center" }}>
                  <CastCard
                    img="/images/beckham-reveal.png"
                    label="THE BROTHER"
                    name="BECKHAM"
                    role="Master of Mayhem"
                    labelColor={colors.secondary}
                    onLabelColor={colors.onSecondary}
                    labelSide="right"
                    labelRotate={3}
                    size="sm"
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "center" }}>
                  <CastCard
                    img="/images/addie-reveal.png"
                    label="THE SISTER"
                    name="ADDIE"
                    role="Two Years of Experience"
                    labelColor={colors.secondary}
                    onLabelColor={colors.onSecondary}
                    labelSide="left"
                    labelRotate={-3}
                    size="sm"
                  />
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* Teaser */}
        <Box
          component="section"
          id="teaser"
          sx={{
            scrollMarginTop: 80,
            py: 12,
            px: { xs: 2, md: 8 },
            bgcolor: colors.primary,
            color: colors.onPrimary,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <VisibilityIcon sx={{ position: "absolute", top: 40, right: 40, opacity: 0.1, fontSize: 300 }} />
          <Container sx={{ maxWidth: "md !important", position: "relative", zIndex: 1, textAlign: "center" }}>
            <Box
              sx={{
                display: "inline-block",
                border: "2px solid rgba(255,255,255,0.3)",
                px: 3,
                py: 1,
                fontFamily: fonts.label,
                letterSpacing: "0.3em",
                fontSize: 14,
                fontWeight: 700,
                mb: 6,
              }}
            >
              TEASER TRAILER
            </Box>
            <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 40, md: 64 }, mb: 4, letterSpacing: "0.02em" }}>
              INTRODUCING THE SPECIAL GUEST...
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 6,
                borderRadius: 6,
                mb: 8,
                backdropFilter: "blur(12px)",
                bgcolor: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "inherit",
              }}
            >
              <Box
                sx={{
                  width: 192,
                  height: 192,
                  mx: "auto",
                  mb: 4,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite",
                  "@keyframes pulse": { "50%": { opacity: 0.5 } },
                }}
              >
                <PersonAddIcon sx={{ fontSize: 96, color: "#fff", opacity: 0.4 }} />
              </Box>
              <Typography sx={{ fontFamily: fonts.body, fontSize: 18, fontWeight: 500, mb: 4, opacity: 0.9 }}>
                A performance so powerful, it hasn't even happened yet. A talent so raw, they're still in the workshop.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                {[
                  { label: "MYSTERY GENRE", rotate: 2 },
                  { label: "GLOBAL REACH", rotate: -1 },
                  { label: "CRITICALLY ACCLAIMED", rotate: 3 },
                ].map((t) => (
                  <Chip
                    key={t.label}
                    label={t.label}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: colors.onPrimary,
                      fontFamily: fonts.label,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      transform: `rotate(${t.rotate}deg)`,
                    }}
                  />
                ))}
              </Stack>
            </Paper>

          </Container>
        </Box>

        {/* Package Tracker */}
        <Box
          component="section"
          id="tracker"
          sx={{
            scrollMarginTop: 80,
            py: 12,
            px: { xs: 2, md: 8 },
            bgcolor: "#EAEDED",
            fontFamily: '"Amazon Ember", "Helvetica Neue", Arial, sans-serif',
          }}
        >
          <Container sx={{ maxWidth: "lg !important" }}>
            {/* Amazon-style header strip */}
            <Box
              sx={{
                bgcolor: "#131A22",
                color: "#fff",
                px: 3,
                py: 1.5,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography sx={{ fontFamily: "inherit", fontWeight: 700, fontSize: 22, color: "#fff" }}>
                amazon<Box component="span" sx={{ color: "#FF9900" }}>.baby</Box>
              </Typography>
              <Typography sx={{ fontFamily: "inherit", color: "#ccc", fontSize: 13, ml: "auto" }}>
                Hello, Dymock Family · Your Orders
              </Typography>
            </Box>

            <Paper
              elevation={2}
              sx={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderRadius: 1,
                p: { xs: 3, md: 5 },
                bgcolor: "#fff",
                fontFamily: "inherit",
                color: "#0F1111",
              }}
            >
              {/* Status header */}
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2} sx={{ mb: 1 }}>
                <Box>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#565959", mb: 0.5 }}>
                    Order #114-{Math.floor(Math.random() * 9000000) + 1000000}-DYMOCK
                  </Typography>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 28, fontWeight: 700, color: "#0F1111" }}>
                    Arriving <Box component="span" sx={{ color: "#067D62" }}>January 2027</Box>
                  </Typography>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 14, color: "#0F1111", mt: 0.5 }}>
                    Your package has shipped. Some assembly required.
                  </Typography>
                </Box>
                <LocalShippingIcon sx={{ fontSize: 64, color: "#FF9900" }} />
              </Stack>

              {/* Progress bar */}
              <Box sx={{ my: 4 }}>
                <Box sx={{ position: "relative", height: 8, bgcolor: "#E7E7E7", borderRadius: 4, mb: 3 }}>
                  <Box sx={{ position: "absolute", top: 0, left: 0, height: "100%", width: "55%", bgcolor: "#067D62", borderRadius: 4 }} />
                </Box>
                <Grid container spacing={1}>
                  {[
                    { label: "Order Placed", sub: "Last spring", done: true },
                    { label: "Preparing", sub: "1st trimester", done: true },
                    { label: "In Transit", sub: "Currently baking", done: true, current: true },
                    { label: "Out for Delivery", sub: "Hospital bag time", done: false },
                    { label: "Delivered", sub: "Welcome to Earth", done: false },
                  ].map((step) => (
                    <Grid item xs={12} sm key={step.label}>
                      <Stack direction={{ xs: "row", sm: "column" }} alignItems={{ xs: "center", sm: "flex-start" }} spacing={1}>
                        {step.done ? (
                          <CheckCircleIcon sx={{ color: step.current ? "#FF9900" : "#067D62", fontSize: 24 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ color: "#999", fontSize: 24 }} />
                        )}
                        <Box>
                          <Typography sx={{ fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: step.done ? "#0F1111" : "#565959" }}>
                            {step.label}
                          </Typography>
                          <Typography sx={{ fontFamily: "inherit", fontSize: 12, color: "#565959" }}>{step.sub}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Item */}
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      bgcolor: "#FFF8DC",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #DDD",
                    }}
                  >
                    <ChildFriendlyIcon sx={{ fontSize: 96, color: "#FF9900" }} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <MuiLink href="#" underline="hover" sx={{ fontFamily: "inherit", color: "#007185", fontSize: 18, fontWeight: 500 }}>
                    Baby Dymock — Tiny Human Edition (2027 Model, Limited Release)
                  </MuiLink>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <StarIcon key={i} sx={{ color: "#FFA41C", fontSize: 18 }} />
                    ))}
                    <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#007185", ml: 1 }}>
                      4,892 ratings (mostly from grandparents)
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 14, color: "#0F1111", mt: 1 }}>
                    <Box component="span" sx={{ color: "#B12704", fontWeight: 700, fontSize: 18 }}>$Priceless</Box>
                    {"  "}
                    <Box component="span" sx={{ textDecoration: "line-through", color: "#565959" }}>$1,000,000 in college tuition</Box>
                  </Typography>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#067D62", fontWeight: 700, mt: 0.5 }}>
                    FREE Prime Delivery · Diapers sold separately
                  </Typography>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#565959", mt: 1 }}>
                    Quantity: 1 &nbsp;·&nbsp; Color: TBD &nbsp;·&nbsp; Size: Surprise
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Delivery info */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#565959", fontWeight: 700, mb: 0.5 }}>
                    Shipping Address
                  </Typography>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 14 }}>
                    The Reveal Residence<br />
                    c/o Matthew & Sadie<br />
                    Utah, USA
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#565959", fontWeight: 700, mb: 0.5 }}>
                    Delivery Instructions
                  </Typography>
                  <Typography sx={{ fontFamily: "inherit", fontSize: 14 }}>
                    Leave at front door. Do NOT ring bell — Beckham is napping.
                    Addie has been notified and will sign for the package.
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Action buttons */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
                {[
                  { label: "Track Package", primary: true },
                  { label: "Cancel Order" },
                  { label: "Return or Replace (Final Sale)" },
                  { label: "Leave Seller Feedback" },
                ].map((btn) => (
                  <Button
                    key={btn.label}
                    sx={{
                      fontFamily: "inherit",
                      textTransform: "none",
                      fontSize: 13,
                      fontWeight: 400,
                      color: "#0F1111",
                      bgcolor: btn.primary ? "#FFD814" : "#F0F2F2",
                      border: btn.primary ? "1px solid #FCD200" : "1px solid #D5D9D9",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.75,
                      boxShadow: "0 2px 5px rgba(15,17,17,0.15)",
                      "&:hover": {
                        bgcolor: btn.primary ? "#F7CA00" : "#E3E6E6",
                      },
                    }}
                  >
                    {btn.label}
                  </Button>
                ))}
              </Stack>

              {/* Footer note */}
              <Box sx={{ mt: 4, p: 2, bgcolor: "#FEF8E7", border: "1px solid #F0D85F", borderRadius: 1, display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <InventoryIcon sx={{ color: "#C77700", fontSize: 20, mt: 0.25 }} />
                <Typography sx={{ fontFamily: "inherit", fontSize: 13, color: "#0F1111" }}>
                  <strong>Heads up:</strong> This item ships via Stork Express. Tracking updates may be sporadic and primarily come in the form of ultrasound photos. No signature required, but tears of joy are encouraged.
                </Typography>
              </Box>
            </Paper>
          </Container>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 12,
          px: { xs: 2, md: 8 },
          textAlign: "center",
          bgcolor: colors.surfaceContainerLowest,
          background: `linear-gradient(to top, rgba(255,215,0,0.2), transparent)`,
        }}
      >
        <Typography sx={{ fontFamily: fonts.display, color: colors.primary, fontSize: 32, mb: 4, letterSpacing: "0.1em" }}>
          THE PREMIERE OF THE CENTURY
        </Typography>
        <Stack direction="row" spacing={4} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 6 }}>
          {["Privacy Policy", "Contact Agent", "Press Kit"].map((l) => (
            <MuiLink
              key={l}
              href="#"
              underline="none"
              sx={{ color: colors.onSurfaceVariant, fontFamily: fonts.body, "&:hover": { color: colors.primary } }}
            >
              {l}
            </MuiLink>
          ))}
        </Stack>
        <Box sx={{ borderTop: "1px solid rgba(112,93,0,0.1)", pt: 6 }}>
          <Typography sx={{ fontFamily: fonts.body, color: colors.onSurfaceVariant, letterSpacing: "0.15em", fontSize: 13 }}>
            © 2026 THE PREMIERE OF THE CENTURY. ALL RIGHTS RESERVED.
          </Typography>
        </Box>
      </Box>

      {/* Mobile Bottom Nav */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 50,
          justifyContent: "space-around",
          alignItems: "center",
          p: 1,
          pb: "env(safe-area-inset-bottom)",
          bgcolor: "rgba(248,249,250,0.9)",
          backdropFilter: "blur(16px)",
          borderTop: "1px solid rgba(255,255,255,0.3)",
          boxShadow: "0 -10px 40px rgba(0,0,0,0.1)",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        {[
          { icon: <MovieIcon />, label: "Premiere", href: "#premiere", active: true },
          { icon: <GroupsIcon />, label: "Cast", href: "#cast" },
          { icon: <VisibilityIcon />, label: "Teaser", href: "#teaser" },
          { icon: <LocalShippingIcon />, label: "Tracker", href: "#tracker" },
        ].map((item) => (
          <Stack
            key={item.label}
            component="a"
            href={item.href}
            alignItems="center"
            justifyContent="center"
            sx={{
              textDecoration: "none",
              color: item.active ? colors.primary : colors.onSurfaceVariant,
              bgcolor: item.active ? "rgba(255,215,0,0.3)" : "transparent",
              borderRadius: 2,
              px: 2,
              py: 0.5,
              "&:active": { transform: "scale(0.9)" },
              transition: "transform .2s",
            }}
          >
            {item.icon}
            <Typography sx={{ fontFamily: fonts.label, fontSize: 10, fontWeight: 700 }}>{item.label}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  )
}

export default BigReveal
